import React, { useState, useEffect, Suspense, lazy } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowUpRight,
    Landmark,
    HeartPulse,
    Factory,
    Zap,
    ShoppingBag,
    Plane,
    GraduationCap,
    Building2,
    Radio,
    Truck,
    Film,
    Building,
    Sparkles,
    X,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import { INDUSTRY_SOLUTIONS } from "../lib/industries";
import { getBfsiSolutionDetail } from "../lib/bfsiSolutionsData";
import { getHealthcareSolutionDetail } from "../lib/healthcareSolutionsData";
import { getManufacturingSolutionDetail } from "../lib/manufacturingSolutionsData";
import { getEnergySolutionDetail } from "../lib/energySolutionsData";
import { getRetailSolutionDetail } from "../lib/retailSolutionsData";
import { getTravelSolutionDetail } from "../lib/travelSolutionsData";
import { getEducationSolutionDetail } from "../lib/educationSolutionsData";
import { getGovernmentSolutionDetail } from "../lib/governmentSolutionsData";
import { getTelecomSolutionDetail } from "../lib/telecomSolutionsData";
import { getLogisticsSolutionDetail } from "../lib/logisticsSolutionsData";
import { getMediaSolutionDetail } from "../lib/mediaSolutionsData";
import { getRealEstateSolutionDetail } from "../lib/realEstateSolutionsData";
import { getCrossIndustryAiSolutionDetail } from "../lib/crossIndustryAiSolutionsData";
import { cn } from "../lib/utils";

const FloatingKnot = lazy(() => import("../components/three/FloatingKnot"));

const ICONS = {
    Landmark,
    HeartPulse,
    Factory,
    Zap,
    ShoppingBag,
    Plane,
    GraduationCap,
    Building2,
    Radio,
    Truck,
    Film,
    Building,
    Sparkles,
};

export default function Products() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initial =
        INDUSTRY_SOLUTIONS.find((ind) => ind.id === searchParams.get("industry"))?.id ||
        INDUSTRY_SOLUTIONS[0].id;
    const [activeId, setActiveId] = useState(initial);
    const [activeModalDetail, setActiveModalDetail] = useState(null);

    // Sync active tab when URL param changes
    useEffect(() => {
        const urlInd = searchParams.get("industry");
        if (urlInd && urlInd !== activeId) {
            const ind = INDUSTRY_SOLUTIONS.find((i) => i.id === urlInd);
            if (ind) {
                setActiveId(ind.id);
                window.setTimeout(() => {
                    document
                        .getElementById("products-industry-detail")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 80);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const active =
        INDUSTRY_SOLUTIONS.find((i) => i.id === activeId) || INDUSTRY_SOLUTIONS[0];
    const ActiveIcon = ICONS[active.icon] || Sparkles;

    const onPick = (id) => {
        setActiveId(id);
        const next = new URLSearchParams(searchParams);
        next.set("industry", id);
        setSearchParams(next, { replace: true });
    };

    return (
        <main data-testid="page-products" className="relative pt-32 md:pt-40">
            <section className="relative pb-14 overflow-hidden">
                <div className="absolute inset-0 -z-10 bg-radial-fade" />
                <div className="absolute right-0 top-0 -z-10 h-[360px] w-[360px] hidden md:block opacity-55">
                    <Suspense fallback={null}>
                        <FloatingKnot color="#1E88E5" accent="#7FD03A" speed={0.2} />
                    </Suspense>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Products & Solutions"
                        title={<>Solutions engineered for your industry.</>}
                        subtitle="Pick an industry to see our deployment ready solution catalog each backed by proven IP, domain tuned AI, and senior practice leads."
                    />
                </div>
            </section>

            {/* INDUSTRY TABS */}
            <section
                data-testid="products-industry-tabs"
                className="relative py-5 sticky top-[72px] z-30 bg-ink-950/85 backdrop-blur-xl border-y border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="flex gap-2 overflow-x-auto edge-fade-x pb-1">
                        {INDUSTRY_SOLUTIONS.map((ind, i) => {
                            const Icon = ICONS[ind.icon] || Sparkles;
                            const isActive = ind.id === activeId;
                            return (
                                <button
                                    key={ind.id}
                                    type="button"
                                    onClick={() => onPick(ind.id)}
                                    data-testid={`industry-tab-${ind.id}`}
                                    className={cn(
                                        "group shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-[12.5px] font-medium transition-all",
                                        isActive
                                            ? "bg-brand-linear text-white border-transparent shadow-glow-brand"
                                            : "glass border-white/10 text-zinc-300 hover:border-white/30"
                                    )}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    <span className="font-mono text-[10px] opacity-70">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{ind.shortName}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ACTIVE INDUSTRY DETAIL */}
            <section
                id="products-industry-detail"
                data-testid={`products-industry-${active.id}`}
                className="relative py-14 md:py-20 scroll-mt-[144px]"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.4 }}
                        >
                            {/* Hero block for the active industry */}
                            <div className="relative overflow-hidden rounded-3xl border border-white/10 glass-strong grid grid-cols-1 lg:grid-cols-12 gap-0">
                                <div className="lg:col-span-7 p-8 md:p-12">
                                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-linear shadow-glow-brand mb-6">
                                        <ActiveIcon className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="font-mono text-xs text-zinc-500">
                                        Industry
                                    </div>
                                    <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tighter leading-[1.05]">
                                        {active.name}
                                    </h2>
                                    <p className="mt-5 text-zinc-300 text-[15.5px] md:text-[16.5px] leading-relaxed max-w-2xl">
                                        {active.overview}
                                    </p>
                                    <div className="mt-7 flex flex-wrap gap-4 items-center">
                                        <Link
                                            to={`/contact?industry=${encodeURIComponent(active.name)}&subject=${encodeURIComponent(active.name + " solutions enquiry")}`}
                                            data-testid={`industry-contact-${active.id}`}
                                            className="btn-glow inline-flex items-center gap-2 rounded-full bg-brand-linear px-6 py-3 text-[14px] font-medium text-white hover:shadow-glow-brand transition-all"
                                        >
                                            Discuss {active.shortName} needs
                                            <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                        <span className="text-[12.5px] text-zinc-500">
                                            {active.solutions.length} solutions ·{" "}
                                            25+ years of leadership
                                        </span>
                                    </div>
                                </div>
                                <div className="relative lg:col-span-5 min-h-[240px] lg:min-h-0 border-t lg:border-t-0 lg:border-l border-white/10 overflow-hidden">
                                    <img
                                        src={active.image}
                                        alt={active.name}
                                        className="absolute inset-0 h-full w-full object-cover opacity-75"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-l from-transparent via-ink-950/40 to-ink-950" />
                                </div>
                            </div>

                            {/* Solutions grid */}
                            <div className="mt-14">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-green mb-4">
                                    Key solutions
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {active.solutions.map((s, i) => {
                                        const solutionDetail =
                                            active.id === "bfsi"
                                                ? getBfsiSolutionDetail(s.title)
                                                : active.id === "healthcare"
                                                    ? getHealthcareSolutionDetail(s.title)
                                                    : active.id === "manufacturing"
                                                        ? getManufacturingSolutionDetail(s.title)
                                                        : active.id === "energy"
                                                            ? getEnergySolutionDetail(s.title)
                                                            : active.id === "retail"
                                                                ? getRetailSolutionDetail(s.title)
                                                                : active.id === "travel"
                                                                    ? getTravelSolutionDetail(s.title)
                                                                    : active.id === "education"
                                                                        ? getEducationSolutionDetail(s.title)
                                                                        : active.id === "government"
                                                                            ? getGovernmentSolutionDetail(s.title)
                                                                            : active.id === "telecom"
                                                                                ? getTelecomSolutionDetail(s.title)
                                                                                : active.id === "logistics"
                                                                                    ? getLogisticsSolutionDetail(s.title)
                                                                                    : active.id === "media"
                                                                                        ? getMediaSolutionDetail(s.title)
                                                                                        : active.id === "real-estate"
                                                                                            ? getRealEstateSolutionDetail(s.title)
                                                                                            : active.id === "ai-foundation"
                                                                                                ? getCrossIndustryAiSolutionDetail(s.title)
                                                                                                : null;
                                        const isClickable = !!solutionDetail;
                                        return (
                                            <motion.article
                                                key={s.title}
                                                initial={{ opacity: 0, y: 20 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, margin: "-40px" }}
                                                transition={{ duration: 0.45, delay: (i % 6) * 0.04 }}
                                                onClick={() => {
                                                    if (solutionDetail) {
                                                        setActiveModalDetail(solutionDetail);
                                                    }
                                                }}
                                                className={cn(
                                                    "group glass rounded-3xl border border-white/5 p-6 md:p-7 brand-border-hover tracing-beam",
                                                    isClickable && "cursor-pointer hover:border-brand-green/30"
                                                )}
                                                data-testid={`solution-${active.id}-${i}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="font-mono text-[11px] text-zinc-500">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </div>
                                                    <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-brand-green transition-colors" />
                                                </div>
                                                <h3 className="mt-5 font-display text-[18px] md:text-[19px] font-semibold tracking-tight leading-tight">
                                                    {s.title}
                                                </h3>
                                                <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed">
                                                    {s.desc}
                                                </p>
                                            </motion.article>
                                        );
                                    })}
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="See it in action"
                        title="Request a tailored demo for your industry."
                        subtitle="Our engineers will customize the walkthrough to your architecture and use-cases."
                    />
                </div>
            </section>

            {/* BFSI Solution Deep-Dive & Compliance Blueprint Modal */}
            <AnimatePresence>
                {activeModalDetail && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActiveModalDetail(null)}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/80 backdrop-blur-md overflow-y-auto"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-4xl bg-ink-900 border border-white/10 rounded-3xl p-6 md:p-10 shadow-glow-brand overflow-y-auto max-h-[90vh] noise-bg my-8 scrollbar-hide"
                        >
                            {/* Close button */}
                            <button
                                onClick={() => setActiveModalDetail(null)}
                                className="absolute top-6 right-6 p-2 rounded-full border border-white/5 hover:border-white/20 bg-white/5 text-zinc-400 hover:text-white transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>

                            {/* Header */}
                            <div>
                                <div className="text-[10px] md:text-[11px] font-mono uppercase tracking-[0.22em] text-brand-green mb-3">
                                    {activeModalDetail.eyebrow}
                                </div>
                                <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight text-white leading-tight">
                                    {activeModalDetail.title}
                                </h2>
                                <p className="mt-6 text-zinc-300 text-[14.5px] md:text-[16px] leading-relaxed">
                                    {activeModalDetail.tagline}
                                </p>
                                <p className="mt-4 text-zinc-400 text-[14.5px] md:text-[15.5px] leading-relaxed">
                                    {activeModalDetail.description}
                                </p>
                            </div>

                            {/* Metrics Grid */}
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {activeModalDetail.metrics.map((metric, idx) => (
                                    <div key={idx} className="glass p-5 rounded-2xl border border-white/5 flex flex-col justify-between">
                                        <div className="text-2xl md:text-3xl font-display font-bold text-gradient-brand">
                                            {metric.value}
                                        </div>
                                        <div className="mt-2.5 text-[11.5px] text-zinc-400 leading-snug">
                                            {metric.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <hr className="my-8 border-white/10" />

                            {/* Problems vs Capabilities */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* The Banking Problem */}
                                <div className="space-y-4">
                                    <h3 className="font-display text-lg md:text-xl font-semibold text-white flex items-center gap-2">
                                        <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                                        {activeModalDetail.problemTitle}
                                    </h3>
                                    <ul className="space-y-3.5">
                                        {activeModalDetail.problems.map((prob, idx) => (
                                            <li key={idx} className="flex gap-3">
                                                <span className="font-mono text-xs text-red-400/80 mt-0.5">{idx + 1}.</span>
                                                <div>
                                                    <h4 className="text-zinc-200 text-[13.5px] font-medium leading-tight">{prob.title}</h4>
                                                    <p className="text-zinc-400 text-[12.5px] mt-1 leading-normal">{prob.desc}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Core Capabilities */}
                                <div className="space-y-4">
                                    <h3 className="font-display text-lg md:text-xl font-semibold text-white flex items-center gap-2">
                                        <CheckCircle2 className="h-5 w-5 text-brand-green shrink-0" />
                                        {activeModalDetail.capabilityTitle}
                                    </h3>
                                    <ul className="space-y-3.5">
                                        {activeModalDetail.capabilities.map((cap, idx) => (
                                            <li key={idx} className="flex gap-3">
                                                <span className="text-brand-green mt-0.5">•</span>
                                                <div>
                                                    <h4 className="text-zinc-200 text-[13.5px] font-medium leading-tight">{cap.title}</h4>
                                                    <p className="text-zinc-400 text-[12.5px] mt-1 leading-normal">{cap.desc}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Action Banner */}
                            <div className="mt-10 p-6 md:p-8 rounded-2xl border border-white/5 bg-white/[0.02] flex flex-col sm:flex-row items-center justify-between gap-6">
                                <div className="text-center sm:text-left">
                                    <h4 className="font-display font-semibold text-white text-[17px]">{activeModalDetail.ctaTitle}</h4>
                                    <p className="text-zinc-400 text-xs mt-1">{activeModalDetail.ctaSubtitle}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                    <Link
                                        to={`/contact?industry=${encodeURIComponent(active.name)}&subject=${encodeURIComponent(activeModalDetail.ctaSubject)}`}
                                        onClick={() => setActiveModalDetail(null)}
                                        className="btn-glow inline-flex items-center justify-center gap-2 rounded-full bg-brand-linear px-6 py-3 text-[13.5px] font-semibold text-white hover:shadow-glow-brand transition-all text-center"
                                    >
                                        Schedule Demo
                                        <ArrowUpRight className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
}

